import { withFormik } from 'formik';
import { I18n } from 'utils/i18n';
import * as Yup from 'yup';

export function assembleTeamForm(values, { authEmployee, isSuperAdmin, ruleCount }) {
  const {
    membersAdded,
    membersRemoved,
    name,
    description,
    parent = null,
    isOptedOut,
    defaultRole,
  } = values;
  const parentId = parent?._id ?? parent ?? null;
  if ((ruleCount || 0) > 0) {
    return {
      name,
      description,
      users: [],
      usersRemoved: [],
      isActive: 1,
      parent: parentId,
      defaultRole,
    };
  }

  const getMember = ({ employee, role, isVisible = false }) => ({
    employee,
    role: role || defaultRole,
    isVisible,
  });

  let users = membersAdded
    .filter(m => isOptedOut[0] !== 'on' || m.employee !== authEmployee._id)
    .map(getMember);
  let usersRemoved = membersRemoved;
  if (!isSuperAdmin && isOptedOut[0] !== 'on') {
    users = [...users, { employee: authEmployee._id, role: 'admin', isVisible: false }];
  } else {
    usersRemoved = [...usersRemoved];
    if (authEmployee?._id) {
      usersRemoved.push(authEmployee._id);
    }
  }

  return {
    name,
    description,
    users,
    usersRemoved,
    isActive: 1,
    parent: parentId,
    defaultRole,
  };
}

class TeamSubmit {
  constructor(values, formikBag) {
    this.formikBag = formikBag;
    this.values = values;
  }

  assembleForm = () =>
    assembleTeamForm(this.values, {
      authEmployee: this.formikBag.props.authEmployee,
      isSuperAdmin: this.formikBag.props.isSuperAdmin,
      ruleCount: this.formikBag.props.team?.ruleCount,
    });

  sendRequest = form => {
    const {
      props: { team, isEdit, actions },
    } = this.formikBag;
    if (isEdit) return actions.teams.update(team._id, form);
    return actions.teams.create(form);
  };

  displayErrors = err => {
    const { name } = this.values;
    const { setErrors } = this.formikBag;

    if (err.status === 409) {
      setErrors({
        server: I18n.t('Resource already exists', { resource: I18n.t('Resources.team'), name }),
      });
    } else if (err.type === 'body') {
      setErrors({ server: I18n.t('Something went wrong with sending data') });
    } else {
      setErrors({ server: I18n.t('Something went wrong with sending data') });
    }
  };
}

export default withFormik({
  validationSchema: Yup.object().shape({
    name: Yup.string()
      .max(100, () => I18n.t('TeamDrawer.Should not exceed', { count: 100 }))
      .trim()
      .required(() => I18n.t('TeamDrawer.Please enter name for the team')),

    description: Yup.string()
      .max(200, () => I18n.t('TeamDrawer.Should not exceed', { count: 200 }))
      .trim(),

    members: Yup.array(),
    defaultRole: Yup.string().required(() =>
      I18n.t('ValidationErrors.Required', { name: I18n.t('TeamDrawer.DefaultRoleField.label') }),
    ),
  }),

  mapPropsToValues: ({ team = {}, isEdit, authEmployee: ae, isSuperAdmin }) => {
    const isMember = team?.users?.some(e => e._id === ae?._id);
    return {
      _id: isEdit ? team._id : '',
      name: isEdit ? team.name : '',
      parent: isEdit ? team.parent : null,
      description: isEdit ? team.description : '',
      members: isEdit
        ? team.users
            ?.filter(_id => _id !== ae?._id)
            .map(u => ({ employee: u._id, role: u.role, isVisible: !!u.isVisible })) || []
        : [],
      optOutDisabled: isSuperAdmin || !ae?.isAdmin || (team.ruleCount || 0) > 0,
      ruleCount: team.ruleCount || 0,
      isOptedOut: isSuperAdmin || (isEdit && !isMember) ? ['on'] : [],
      membersRemoved: [],
      membersAdded: [],
      defaultRole: isEdit ? team.defaultRole : '',
    };
  },

  handleSubmit: async (values, formikBag) => {
    const {
      props: { team, actions, onClose },
      setSubmitting,
    } = formikBag;

    const handler = new TeamSubmit(values, formikBag);

    const form = handler.assembleForm();

    const parentId = form.parent?._id ?? form.parent;
    if (team?._id && parentId && String(parentId) === String(team._id)) {
      setSubmitting(false);
      formikBag.setErrors({
        parent: I18n.t('TeamDrawer.ParentCannotBeSelf'),
      });
      return;
    }

    try {
      await handler.sendRequest(form);

      setSubmitting(false);

      if (team?._id) {
        actions.invalidateTeamsTags([
          {
            type: 'Teams',
            id: team._id,
          },
        ]);
      }

      if (onClose) onClose();
      else actions.drawers.team.close();
    } catch (err) {
      setSubmitting(false);
      handler.displayErrors(err);
    }
  },
  displayName: 'ChannelForm',
  enableReinitialize: true,
});
